// frontend/src/utils/workforceUtils.js
//
// Workforce-domain helpers for the TIME-001+ web frontend.
// Storage convention: hourlyRate is in CENTS on the wire (data-models §17.1).
// Display convention: dollars-and-cents string with leading $.

import moment from "moment-timezone";

/**
 * Phoenix (no-DST) is the studio's authoritative timezone for every
 * user-facing time. CLAUDE.md §"Design Tokens": "All times displayed in
 * America/Phoenix timezone regardless of device locale. Always label times
 * 'MST' in the UI."
 */
export const STUDIO_TZ = "America/Phoenix";
export const STUDIO_TZ_LABEL = "MST";

/**
 * Render an ISO/Date as "HH:MM AM/PM MST" — the format every clock screen
 * status line uses (TIME-004a §"Body (clocked in)" + TIME-005 §"Region 1").
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export const formatStudioTime = (value) => {
  if (!value) return "";
  const m = moment.tz(value, STUDIO_TZ);
  if (!m.isValid()) return "";
  return `${m.format("h:mm A")} ${STUDIO_TZ_LABEL}`;
};

/**
 * Render an elapsed duration since the supplied start instant as "HHh MMm"
 * (e.g., "03h 18m"). Returns "00h 00m" when start is in the future or
 * malformed.
 * @param {string|Date|null|undefined} startInstant
 * @param {Date|null|undefined} [now]  — injectable for tests; defaults to new Date()
 * @returns {string}
 */
export const formatElapsedSince = (startInstant, now) => {
  if (!startInstant) return "00h 00m";
  const startMs = new Date(startInstant).getTime();
  if (Number.isNaN(startMs)) return "00h 00m";
  const nowMs = (now instanceof Date ? now : new Date()).getTime();
  const diffMs = Math.max(0, nowMs - startMs);
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}h ${String(minutes).padStart(
    2,
    "0"
  )}m`;
};

/**
 * Convert an integer cents value to a "$X.XX" display string.
 * Returns "" for null/undefined so input fields render empty.
 * @param {number|null|undefined} cents
 * @returns {string}
 */
export const formatCentsAsCurrency = (cents) => {
  if (cents === null || cents === undefined || cents === "") {
    return "";
  }
  const numeric = Number(cents);
  if (Number.isNaN(numeric)) {
    return "";
  }
  return `$${(numeric / 100).toFixed(2)}`;
};

/**
 * Parse a user-entered dollars string ("12.50", "$12.50", "12") into integer cents.
 * Returns null if the input cannot be parsed.
 * @param {string|number} dollarsInput
 * @returns {number|null}
 */
export const parseCurrencyToCents = (dollarsInput) => {
  if (dollarsInput === null || dollarsInput === undefined || dollarsInput === "") {
    return null;
  }
  const cleaned = String(dollarsInput).replace(/[^0-9.]/g, "");
  if (!cleaned) {
    return null;
  }
  const dollars = parseFloat(cleaned);
  if (Number.isNaN(dollars)) {
    return null;
  }
  // Round to avoid floating-point drift (e.g., 27.05 * 100 === 2704.9999...).
  return Math.round(dollars * 100);
};

/**
 * The four canonical employmentType enum values per data-models §17.1.
 * Order is the display order on dropdowns (W-2 first, then 1099, then generic).
 */
export const EMPLOYMENT_TYPES = [
  {
    value: "full_time",
    label: "W-2 Full-time",
    helper: "Eligible for OT",
    chipTint: "gold",
  },
  {
    value: "part_time",
    label: "W-2 Part-time",
    helper: "Eligible for OT",
    chipTint: "gold-light",
  },
  {
    value: "contractor_1099",
    label: "1099 Contractor",
    helper: "Straight-time only",
    chipTint: "teal",
  },
  {
    value: "contractor",
    label: "Contractor",
    helper: "Straight-time only",
    chipTint: "gray",
  },
];

/**
 * Look up the display label for an employmentType enum value.
 * @param {string} value
 * @returns {string}
 */
export const getEmploymentTypeLabel = (value) => {
  const found = EMPLOYMENT_TYPES.find((t) => t.value === value);
  return found ? found.label : value || "";
};

/**
 * Returns true if the given user role is admin-tier (can read hourlyRate, can
 * mutate StaffProfile records). Per data-models §17.1.2 the manager role is a
 * deferred decision; v1 collapses to "all admin" so this helper is intentionally
 * narrow. Update here if a manager role lands in `User.role`.
 * @param {string|undefined} role
 * @returns {boolean}
 */
export const isAdminRole = (role) => role === "admin";

/**
 * Render an ISO date string (or Date) as YYYY-MM-DD for date inputs.
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export const toDateInputValue = (value) => {
  if (!value) {
    return "";
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
